import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const InnovationsAcademyCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Innovations Academy - Virtual | Case Study | Curipod"
      subTitle="How a K-8 virtual school in California's Palmdale School District achieved double-digit ELA and Math growth, 95% attendance, and the highest test participation in their district with Curipod."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>Double-Digit Growth</HeroTitle>
            <HeroSubtitle>Innovations Academy &mdash; Virtual</HeroSubtitle>
            <HeroDescription>
              How a K-8 virtual school in California&apos;s Palmdale School District
              turned rolling enrollment and virtual learning challenges into measurable
              ELA and Math gains, 95% attendance, and the highest test participation in
              their district &mdash; with Curipod.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Palmdale School District, CA</MetaBadge>
              <MetaBadge>K-8 Virtual School</MetaBadge>
              <MetaBadge>2024–2025 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    +19<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>7th Grade ELA Growth</StatLabel>
                  <StatYear>2023–2024 → 2024–2025</StatYear>
                </StatItem>
                <ArrowContainer>|</ArrowContainer>
                <StatItem>
                  <StatNumber>
                    +17<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>7th Grade Math Growth</StatLabel>
                  <StatYear>2023–2024 → 2024–2025</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    95<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Attendance Rate</StatLabel>
                  <StatYear>Only school in district to reach 95%</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>A VIRTUAL SCHOOL WITH BIG GOALS</SectionTag>
              <SectionTitle>Rolling Enrollment, Rising Expectations</SectionTitle>
              <SectionText>
                Innovations Academy, a K-8 virtual school in California&apos;s Palmdale
                School District, serves students who need an alternative learning
                environment &mdash; whether for personal circumstances, anxiety, or the
                fit of online learning. Students can enter at any time of the year,
                creating both challenges and opportunities.
              </SectionText>
              <SectionText>
                The team discovered Curipod through EduProtocols, well-known lesson frames
                designed to spark critical thinking, communication, collaboration, and
                creativity. Curipod quickly became the perfect complement, helping
                Innovations move from the monotony of LMS slides to lessons that built
                community, confidence, and engagement.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  We have rolling enrollment&hellip; I can get a student that&apos;s
                  starting today.
                </QuoteText>
                <QuoteAttrib>— Aaron Boenigk, Teacher</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamCardTitle>The Innovations Academy Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>Teacher</TeamRole>
                  <TeamName>Aaron Boenigk</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Co-Teacher</TeamRole>
                  <TeamName>Megann Kane</TeamName>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Principal</TeamRole>
                  <TeamName>Melissa Garcia</TeamName>
                  <TeamNote>
                    &ldquo;Only school in our district to hit 95% attendance&rdquo;
                  </TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Administrator</TeamRole>
                  <TeamName>Amber Godinez</TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* FROM PARTICIPATION TO PROGRESS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              FROM PARTICIPATION TO PROGRESS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              Measurable Gains Across the Board
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              For virtual schools, even maintaining steady test participation can be a
              challenge. With students enrolling and exiting year-round, any measurable
              gains are significant. At Innovations Academy, those gains weren&apos;t just
              visible &mdash; they were substantial. They posted the highest participation
              in their district, along with measurable growth in both ELA and Math.
            </SectionText>

            <GrowthTable>
              <thead>
                <tr>
                  <Th>Grade</Th>
                  <Th>ELA Growth</Th>
                  <Th>Math Growth</Th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <Td>5th</Td>
                  <Td $positive>+2%</Td>
                  <Td $positive>+10%</Td>
                </tr>
                <tr>
                  <Td>6th</Td>
                  <Td $positive>+10%</Td>
                  <Td $positive>+12%</Td>
                </tr>
                <tr>
                  <Td>7th</Td>
                  <Td $positive $bold>
                    +19%
                  </Td>
                  <Td $positive $bold>
                    +17%
                  </Td>
                </tr>
              </tbody>
            </GrowthTable>
            <TableCaption>Year-over-year growth from 2023–24 to 2024–25</TableCaption>

            <FocusAreaGrid>
              {[
                'Engagement in Virtual Classrooms',
                'Peer-to-Peer Interaction',
                'Writing Growth',
                'Test Prep',
              ].map(area => (
                <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
              ))}
            </FocusAreaGrid>

            <BlockQuote style={{ maxWidth: 620, margin: '40px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                We were the only school in our district to hit 95% attendance.
              </QuoteText>
              <QuoteAttrib>— Melissa Garcia, Principal</QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* HOW CURIPOD MADE A DIFFERENCE — 5 IMPACT AREAS */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            HOW CURIPOD MADE A DIFFERENCE
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Five Ways Curipod Changed the Classroom
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>💬</ImpactIcon>
              <ImpactTitle>Immediate, Actionable Feedback</ImpactTitle>
              <ImpactQuote>
                &ldquo;The immediate feedback for the students is super beneficial&hellip;
                I&apos;m able to get through a lot of things a lot quicker.&rdquo;
              </ImpactQuote>
              <ImpactSubQuote>
                &ldquo;This generation needs instant everything, so getting that feedback
                right away means they&apos;re not waiting for me to read six other
                kids&apos; first.&rdquo;
              </ImpactSubQuote>
              <ImpactAttribRow>
                <ImpactAttrib>— Aaron Boenigk &amp; Megann Kane</ImpactAttrib>
              </ImpactAttribRow>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Engagement in a Virtual Setting</ImpactTitle>
              <ImpactQuote>
                &ldquo;Curipod is one of the tools we use to increase engagement. Kids
                with cameras off still participate.&rdquo;
              </ImpactQuote>
              <ImpactSubQuote>
                &ldquo;They get proud when they hit top three, and many return after class
                to improve their score.&rdquo;
              </ImpactSubQuote>
              <ImpactAttribRow>
                <ImpactAttrib>— Amber Godinez &amp; Megann Kane</ImpactAttrib>
              </ImpactAttribRow>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>📝</ImpactIcon>
              <ImpactTitle>Building Test Prep Stamina</ImpactTitle>
              <ImpactQuote>
                &ldquo;Curipod creates sources to read and walks them through exactly what
                it&apos;ll look like on the test.&rdquo;
              </ImpactQuote>
              <ImpactSubQuote>
                &ldquo;This pushes them to write longer.&rdquo;
              </ImpactSubQuote>
              <ImpactAttribRow>
                <ImpactAttrib>— Amber Godinez &amp; Megann Kane</ImpactAttrib>
              </ImpactAttribRow>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🔄</ImpactIcon>
              <ImpactTitle>Flexibility Across Subjects</ImpactTitle>
              <ImpactQuote>
                &ldquo;We ran a session with 75 kids. We use it in math with the
                whiteboard, in science, in history &mdash; the same platform across
                subjects.&rdquo;
              </ImpactQuote>
              <ImpactSubQuote>
                &ldquo;Curipod lets us break the monotony from the LMS &lsquo;next page,
                next page.&rsquo;&rdquo;
              </ImpactSubQuote>
              <ImpactAttribRow>
                <ImpactAttrib>— Aaron Boenigk &amp; Amber Godinez</ImpactAttrib>
              </ImpactAttribRow>
            </ImpactCard>

            <ImpactCard $wide>
              <ImpactIcon>✨</ImpactIcon>
              <ImpactTitle>A Culture of Revision</ImpactTitle>
              <ImpactQuote>
                &ldquo;Students come back on their own time to revise because they want
                that higher score.&rdquo;
              </ImpactQuote>
              <SectionText style={{ fontSize: 16, marginBottom: 12 }}>
                What began as teacher-directed feedback became student-driven improvement
                &mdash; a rare and powerful change, especially in virtual learning.
              </SectionText>
              <ImpactAttribRow>
                <ImpactAttrib>— Megann Kane</ImpactAttrib>
              </ImpactAttribRow>
            </ImpactCard>
          </ImpactGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              More Than a Teaching Tool
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              For Innovations Academy, Curipod isn&apos;t just a teaching tool; it&apos;s
              a bridge. It keeps students engaged, gives teachers instant evidence of
              learning, and fosters accountability alongside community.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                It is a game changer. Instant feedback is critical for buy-in, and
                attendance has gone up &mdash; kids ask, &lsquo;Can we do a
                Curipod?&rsquo;
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Megann Kane, Co-Teacher
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              With rolling enrollment and students facing personal or academic hurdles,
              any measurable gain is significant. Innovations Academy didn&apos;t just
              maintain &mdash; they grew. <strong>+19% in 7th-grade ELA</strong>,{' '}
              <strong>+17% in 7th-grade Math</strong>, and the{' '}
              <strong>highest participation in their district</strong>.
            </ResultNote>

            <ResultsImageRow>
              <ResultsImageWrap>
                <Image
                  src="/case-study-innovations-academy/curipod-lesson.png"
                  width={577}
                  height={357}
                  alt="Curipod lesson showing student responses and voting in a virtual classroom"
                  style={{ borderRadius: 12 }}
                />
              </ResultsImageWrap>
              <ResultsImageWrap>
                <Image
                  src="/case-study-innovations-academy/caaspp-test-prep.png"
                  width={488}
                  height={261}
                  alt="CAASPP Performance Task — Informative/Explanatory Composition prepared in Curipod"
                  style={{ borderRadius: 12 }}
                />
              </ResultsImageWrap>
            </ResultsImageRow>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              THE BIGGER PICTURE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              A Bridge to Engagement
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              For a virtual school where students can join at any point in the year,
              building connection and community is everything. Curipod gave Innovations
              Academy the tools to make every student feel seen, heard, and challenged
              &mdash; no matter when they enrolled.
            </SectionText>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                Curipod lets us break the monotony. Kids with cameras off still
                participate &mdash; and they come back on their own time to revise.
              </FinalQuoteText>
              <QuoteAttrib>— Innovations Academy Teaching Team</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {[
                'Engagement',
                'Virtual Learning',
                'Test Prep',
                'Revision',
                'Feedback',
              ].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Innovations Academy case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Innovations_Academy.pdf"
              download="Curipod_Case_Study_Innovations_Academy.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default InnovationsAcademyCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: rgba(255, 255, 255, 0.4);
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── GROWTH TABLE ──────────────────────────────────────────────────────────── */

const GrowthTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  max-width: 560px;
  margin: 0 auto 32px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: ${theme.effects.defaultShadow};
`;

const Th = styled.th`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 14px;
  font-weight: bold;
  letter-spacing: 1px;
  padding: 14px 20px;
  text-align: center;

  &:first-child {
    text-align: left;
  }
`;

const Td = styled.td<{ $positive?: boolean; $bold?: boolean }>`
  background: ${theme.colors.white};
  color: ${({ $positive }) => ($positive ? '#2e7d32' : theme.colors.black)};
  font-size: 16px;
  font-weight: ${({ $bold }) => ($bold ? 'bold' : '500')};
  padding: 14px 20px;
  text-align: center;
  border-bottom: 1px solid ${theme.colors.beigeDark};

  &:first-child {
    text-align: left;
    font-weight: 600;
    color: ${theme.colors.black};
  }
`;

const TableCaption = styled.p`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  text-align: center;
  margin: 0 0 32px;
  font-style: italic;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactSubQuote = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttribRow = styled.div`
  margin-top: 4px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 48px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const ResultsImageRow = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const ResultsImageWrap = styled.div`
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  max-width: 400px;

  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
